// Live clock — derives its display from FACTS rendered by shared/_live_clock:
//   base    — summed seconds of completed entries in the scope
//   running — the open entries (who / which task / since when)
// Display = base + Σ(now − started_at), recomputed every second from the
// wall clock, so the value can never drift: laptop sleep, suspended tabs,
// and multi-runner scopes (N runners advance the total N× real time) all
// come out right for free. There is NO polling — when the facts change,
// TimeEntry's after-commit hooks broadcast a Turbo Stream replace of this
// element, Stimulus reconnects the fresh element, and ticking resumes.
//
// Hovering a clock with active runners opens a popover listing who's
// tracking, each name linking to their task in the project browser.
import { Controller } from "@hotwired/stimulus"

interface Runner {
  name: string
  user_id: number
  task_id: number
  task_name: string
  started_at: string
}

// ----- Shared clock skew -----
// ONE page-wide offset (client clock minus server clock), refreshed on
// every connect from the element's ms-precision server_now. Per-element
// skews would give each clock a slightly different idea of "now" — with
// second-precision timestamps that difference reached a full second, and
// two clocks over the same entry (session timer vs popover) could floor
// to different seconds and permanently disagree by one.
let sharedSkewMs = 0
let skewKnown = false

// ----- Shared 1s ticker (one interval for every clock on the page) -----

const active = new Set<LiveClockController>()
let ticker: number | null = null

function ensureTicker(): void {
  if (ticker != null) return
  ticker = window.setInterval(() => {
    // One `now` for the whole tick, so every clock and the popover derive
    // from the same instant and always agree.
    const now = Date.now() - sharedSkewMs
    active.forEach(c => c.render(now))
    if (popoverFor) popoverFor.renderPopoverTimes(now)
  }, 1000)
}

function releaseTicker(): void {
  if (active.size === 0 && ticker != null) {
    window.clearInterval(ticker)
    ticker = null
  }
}

// ----- Singleton popover -----

let popoverEl: HTMLElement | null = null
let popoverFor: LiveClockController | null = null
let hideTimer: number | null = null

function ensurePopover(): HTMLElement {
  if (!popoverEl || !popoverEl.isConnected) {
    popoverEl = document.createElement("div")
    popoverEl.className =
      "fixed z-[2100] min-w-[240px] max-w-[320px] rounded-xl border border-[var(--color-surface-border-2)] " +
      "bg-gradient-to-b from-[var(--color-surface-2)] to-[var(--color-surface-1)] " +
      "shadow-[0_20px_50px_rgba(0,0,0,0.6)] p-3 text-[12.5px] leading-snug"
    popoverEl.style.display = "none"
    // Keep it open while the pointer is over the card itself.
    popoverEl.addEventListener("mouseenter", () => {
      if (hideTimer != null) { window.clearTimeout(hideTimer); hideTimer = null }
    })
    popoverEl.addEventListener("mouseleave", scheduleHide)
    document.body.appendChild(popoverEl)
  }
  return popoverEl
}

function scheduleHide(): void {
  if (hideTimer != null) window.clearTimeout(hideTimer)
  hideTimer = window.setTimeout(hidePopover, 150)
}

function hidePopover(): void {
  if (popoverEl) popoverEl.style.display = "none"
  popoverFor = null
}

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && popoverFor) hidePopover()
})
// Never let an open popover (or its body-attached element) leak into
// Turbo's page snapshot.
document.addEventListener("turbo:before-cache", () => {
  hidePopover()
  popoverEl?.remove()
  popoverEl = null
})

// ----- The controller -----

export default class LiveClockController extends Controller<HTMLElement> {
  static values = {
    base: Number,
    runners: Array,
    format: { type: String, default: "clock" },
    estimate: { type: Number, default: 0 },
    serverNow: String,
    // Default FALSE, because Rails drops data-* attributes whose value is
    // `false` — the partial only emits the attribute when the popover is
    // wanted, so "attribute absent" must mean "no popover".
    popover: { type: Boolean, default: false },
  }
  declare readonly baseValue: number
  declare readonly runnersValue: Runner[]
  declare readonly formatValue: string
  declare readonly estimateValue: number
  declare readonly serverNowValue: string
  declare readonly popoverValue: boolean

  private enterHandler = () => this.showPopover()
  private leaveHandler = () => scheduleHide()

  connect(): void {
    // Refresh the page-wide skew from this element's ms-precision
    // server_now (rendered moments ago, either at page load or by a
    // broadcast). All clocks share it — see sharedSkewMs above.
    const serverMs = Date.parse(this.serverNowValue)
    if (Number.isFinite(serverMs)) {
      sharedSkewMs = Date.now() - serverMs
      skewKnown = true
    } else if (!skewKnown) {
      sharedSkewMs = 0
    }
    this.render()
    if (this.runnersValue.length > 0) {
      active.add(this)
      ensureTicker()
      // The green underline + hover card only make sense where "who is
      // tracking" is a real question — clocks that can only ever be the
      // viewer (session timer, Today / This-week cards) opt out via
      // popover: false and just tick.
      if (this.popoverValue) {
        this.element.classList.add("has-runners")
        this.element.addEventListener("mouseenter", this.enterHandler)
        this.element.addEventListener("mouseleave", this.leaveHandler)
      }
    }
  }

  disconnect(): void {
    active.delete(this)
    releaseTicker()
    if (popoverFor === this) hidePopover()
    this.element.removeEventListener("mouseenter", this.enterHandler)
    this.element.removeEventListener("mouseleave", this.leaveHandler)
  }

  // ----- Rendering -----
  // `now` is server-time ms, passed in by the shared ticker so every clock
  // on the page derives from the same instant; out-of-band renders
  // (connect, popover open) compute their own.

  private totalSeconds(now: number): number {
    return this.runnersValue.reduce((sum, r) => {
      const started = Date.parse(r.started_at)
      return sum + (Number.isFinite(started) ? Math.max(0, (now - started) / 1000) : 0)
    }, this.baseValue)
  }

  render(now: number = Date.now() - sharedSkewMs): void {
    const seconds = this.totalSeconds(now)
    let text: string
    if (this.formatValue === "hours") {
      text = (seconds / 3600).toFixed(3)
    } else if (this.formatValue === "percent") {
      text = this.estimateValue > 0 ? (seconds / this.estimateValue / 36).toFixed(1) + "%" : ""
    } else {
      text = formatClock(seconds)
    }
    if (this.element.textContent !== text) this.element.textContent = text
  }

  // ----- Popover -----

  private showPopover(): void {
    if (hideTimer != null) { window.clearTimeout(hideTimer); hideTimer = null }
    popoverFor = this
    // Re-render every clock and the popover from ONE instant — otherwise
    // clocks still show the previous tick's value and the freshly-opened
    // popover could disagree by one second until the next tick.
    const now = Date.now() - sharedSkewMs
    active.forEach(c => c.render(now))
    const el = ensurePopover()
    // The runner's NAME is plain text; the TASK is the link — clicking
    // through goes to what they're working on, not to a person. Both
    // truncate together so long names/tasks never widen the card.
    const rows = this.runnersValue.map((r, i) => `
      <div class="flex items-baseline gap-2 py-1 ${i > 0 ? "border-t border-[var(--color-surface-border)]/60" : ""}">
        <span class="w-1.5 h-1.5 rounded-full bg-[var(--color-state-green-500)] shrink-0 self-center"></span>
        <span class="min-w-0 flex-1 truncate whitespace-nowrap">
          <span class="font-medium text-[var(--color-ink)]">${escapeHtml(r.name)}</span>
          <span class="text-[var(--color-ink-muted)]"> · </span><a href="/project-browser?context=t${r.task_id}" title="${escapeHtml(r.task_name)}"
             class="text-[var(--color-ink-muted)] underline decoration-dotted decoration-[var(--color-brand-500)] underline-offset-2 hover:text-[var(--color-brand-500)]">${escapeHtml(r.task_name)}</a>
        </span>
        <span class="font-mono text-[11px] text-[var(--color-state-green-500)] shrink-0" data-runner-since="${r.started_at}"></span>
      </div>`).join("")
    el.innerHTML = `
      <div class="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-ink-muted)] mb-1.5">Currently tracking</div>
      ${rows}`
    el.style.display = "block"
    this.renderPopoverTimes(now)
    // Position under the clock, clamped to the viewport.
    const rect = this.element.getBoundingClientRect()
    const width = el.offsetWidth
    el.style.top = `${Math.min(rect.bottom + 8, window.innerHeight - el.offsetHeight - 8)}px`
    el.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`
  }

  renderPopoverTimes(now: number = Date.now() - sharedSkewMs): void {
    if (popoverFor !== this || !popoverEl) return
    popoverEl.querySelectorAll<HTMLElement>("[data-runner-since]").forEach(span => {
      const started = Date.parse(span.dataset.runnerSince ?? "")
      if (Number.isFinite(started)) span.textContent = formatClock(Math.max(0, (now - started) / 1000))
    })
  }
}

function formatClock(seconds: number): string {
  const s = Math.floor(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
}

function escapeHtml(text: string): string {
  const div = document.createElement("div")
  div.textContent = text
  return div.innerHTML
}
